import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  useCancelSubscription,
  useGameOwnerships,
  useMe,
  useSubscriptionChange,
  useSubscriptionPlans,
  useSubscriptions,
  usePlayHistory,
  useTournamentHistory,
} from "../api/api_hooks";
import PlaytimeHistory from "../components/PlaytimeHistory";
import { paymentsEnabled } from "../featureFlags";
import { useRemotePage } from "../remote/RemoteControlContext";

const activeStatuses = new Set(["ACTIVE", "TRIALING"]);

function listFromResponse(data) {
  if (!data) return [];
  return Array.isArray(data) ? data : data.results || [];
}

function formatDate(value) {
  if (!value) return "No expiry";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatPrice(price) {
  if (!price) return "";
  const amount = Number(price.amount || 0);
  const currency = String(price.currency || "USD").toUpperCase();
  const formattedAmount = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  const symbols = { USD: "$", CAD: "$", EUR: "EUR " };
  return symbols[currency] ? `${symbols[currency]}${formattedAmount}` : `${formattedAmount} ${currency}`;
}

function priceAmount(price) {
  return Number(price?.amount || 0);
}

function statusClasses(status) {
  if (activeStatuses.has(status)) {
    return "border-emerald-300/30 bg-emerald-400/10 text-emerald-200";
  }
  if (status === "PAST_DUE" || status === "UNPAID") {
    return "border-yellow-300/30 bg-yellow-300/10 text-yellow-100";
  }
  return "border-white/10 bg-white/5 text-indigo-100/75";
}

function subscriptionIsActive(subscription) {
  if (!activeStatuses.has(subscription.status)) return false;
  const endDate = subscription.time_validator?.end_date;
  return !endDate || new Date(endDate) > new Date();
}

function buildUpgradeOptions(subscription, plans) {
  if (!subscription) return [];

  const currentPlan = subscription.plan || {};
  const currentBillingInterval = subscription.billing_interval || "month";
  const currentMonthlyPrice = priceAmount(currentPlan.price_per_month);
  const pendingPlanId = subscription.pending_subscription_plan?.id;
  const pendingBillingInterval = subscription.pending_billing_interval;
  const optionIsPending = (planId, billingInterval) =>
    pendingPlanId === planId && pendingBillingInterval === billingInterval;

  const options = [];
  if (currentBillingInterval === "month" && currentPlan.id) {
    options.push({
      key: `${currentPlan.id}-year`,
      label: optionIsPending(currentPlan.id, "year") ? "Yearly Upgrade Scheduled" : "Upgrade to Yearly",
      planId: currentPlan.id,
      billingInterval: "year",
      disabled: optionIsPending(currentPlan.id, "year"),
    });
  }

  plans
    .filter((plan) => plan.id !== currentPlan.id && priceAmount(plan.price_per_month) > currentMonthlyPrice)
    .sort((a, b) => priceAmount(a.price_per_month) - priceAmount(b.price_per_month))
    .forEach((plan) => {
      const pending = optionIsPending(plan.id, currentBillingInterval);
      options.push({
        key: `${plan.id}-${currentBillingInterval}`,
        label: pending ? `${plan.name} Upgrade Scheduled` : `Upgrade to ${plan.name}`,
        planId: plan.id,
        billingInterval: currentBillingInterval,
        disabled: pending,
      });
    });

  return options;
}

function SubscriptionCard({
  subscription,
  active,
  upgradeOptions = [],
  isCanceling = false,
  isChanging = false,
  changingOptionKey = null,
  billingChangesEnabled = true,
  onCancel,
  onUpgradeOption,
}) {
  const plan = subscription.plan || {};
  const timeValidator = subscription.time_validator || {};
  const billingInterval = subscription.billing_interval ? `${subscription.billing_interval}ly` : "Not recorded";
  const cancellationPending = Boolean(subscription.cancel_at_period_end);

  return (
    <article className="overflow-hidden rounded-3xl border border-white/10 bg-indigo-950/45 text-left shadow-xl shadow-slate-950/25 backdrop-blur">
      {plan.image ? (
        <div className="h-52 bg-slate-950">
          <img src={plan.image} alt={plan.name || "Subscription plan"} className="h-full w-full object-cover" />
        </div>
      ) : null}

      <div className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-sky-200/70">
              {active ? "Active subscription" : "Previous subscription"}
            </p>
            <h2 className="mt-3 text-2xl font-black text-white">{plan.name || "Subscription"}</h2>
          </div>
          <span className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.22em] ${statusClasses(subscription.status)}`}>
            {subscription.status || "Unknown"}
          </span>
        </div>

        {plan.description ? (
          <p className="mt-4 text-sm leading-6 text-indigo-100/75">{plan.description}</p>
        ) : null}
        {active && cancellationPending ? (
          <p className="mt-4 rounded-2xl border border-yellow-300/25 bg-yellow-300/10 px-4 py-3 text-sm font-semibold text-yellow-100">
            Cancellation is scheduled. You keep access until {formatDate(timeValidator.end_date)}.
          </p>
        ) : null}

        <dl className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <dt className="text-xs uppercase tracking-[0.25em] text-indigo-200/65">Started</dt>
            <dd className="mt-2 font-semibold text-white">{formatDate(timeValidator.start_date)}</dd>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <dt className="text-xs uppercase tracking-[0.25em] text-indigo-200/65">Ends</dt>
            <dd className="mt-2 font-semibold text-white">{formatDate(timeValidator.end_date)}</dd>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <dt className="text-xs uppercase tracking-[0.25em] text-indigo-200/65">Sessions</dt>
            <dd className="mt-2 font-semibold text-white">{plan.active_sessions_limit || 1}</dd>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <dt className="text-xs uppercase tracking-[0.25em] text-indigo-200/65">Billing</dt>
            <dd className="mt-2 font-semibold capitalize text-white">{billingInterval}</dd>
          </div>
        </dl>

        {active && billingChangesEnabled ? (
          <div className="mt-6 flex flex-wrap gap-3">
            {upgradeOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => onUpgradeOption(option)}
                disabled={isChanging || option.disabled}
                className="rounded-full bg-sky-300 px-6 py-3 text-sm font-black uppercase tracking-[0.25em] text-slate-950 transition hover:bg-cyan-200 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-indigo-100/50"
              >
                {isChanging && changingOptionKey === option.key ? "Scheduling..." : option.label}
              </button>
            ))}
            <button
              type="button"
              onClick={onCancel}
              disabled={isCanceling || cancellationPending}
              className="rounded-full border border-pink-300/40 px-6 py-3 text-sm font-black uppercase tracking-[0.25em] text-pink-100 transition hover:border-pink-200 hover:bg-pink-400/10 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {cancellationPending ? "Cancellation Scheduled" : isCanceling ? "Scheduling..." : "Cancel Subscription"}
            </button>
          </div>
        ) : null}
        {active && !billingChangesEnabled ? (
          <p className="mt-6 rounded-2xl border border-yellow-300/25 bg-yellow-300/10 px-4 py-3 text-sm font-semibold text-yellow-100">
            Online billing changes are disabled during the beta. Ask an administrator to update this subscription.
          </p>
        ) : null}
      </div>
    </article>
  );
}

function OwnedGameCard({ ownership }) {
  const game = ownership.game || {};

  return (
    <article className="overflow-hidden rounded-3xl border border-white/10 bg-indigo-950/40 text-left shadow-lg shadow-slate-950/25">
      <div className="h-36 bg-slate-900">
        {game.cover_image ? (
          <img src={game.cover_image} alt={game.title} className="h-full w-full object-cover" />
        ) : null}
      </div>
      <div className="p-5">
        <h3 className="text-lg font-black text-white">{game.title || "Owned game"}</h3>
        <div className="mt-4 flex items-center justify-between gap-3 text-sm text-indigo-100/75">
          <span>{formatPrice(game.price)}</span>
          <span>{formatDate(ownership.invoice?.time_validator?.start_date)}</span>
        </div>
      </div>
    </article>
  );
}

export default function AccountPage() {
  const navigate = useNavigate();
  const { data: me, isLoading: isMeLoading } = useMe();
  const subscriptionsQuery = useSubscriptions({ enabled: Boolean(me) });
  const ownershipsQuery = useGameOwnerships({ enabled: Boolean(me) });
  const plansQuery = useSubscriptionPlans({ enabled: Boolean(me) });
  const playHistoryQuery = usePlayHistory({ enabled: Boolean(me) });
  const tournamentHistoryQuery = useTournamentHistory({ enabled: Boolean(me) });
  const cancelSubscription = useCancelSubscription();
  const subscriptionChange = useSubscriptionChange();
  const [changingOptionKey, setChangingOptionKey] = React.useState(null);

  const subscriptions = listFromResponse(subscriptionsQuery.data);
  const ownerships = listFromResponse(ownershipsQuery.data);
  const plans = listFromResponse(plansQuery.data);
  const playHistory = (playHistoryQuery.data || []).filter((session) => session.rounds?.length);
  const tournamentHistory = (tournamentHistoryQuery.data || []).filter((run) => run.sessions?.length);
  const activeSubscriptions = subscriptions.filter(subscriptionIsActive);
  const previousSubscriptions = subscriptions.filter((sub) => !subscriptionIsActive(sub));
  const activeSubscription = activeSubscriptions[0] || null;
  const upgradeOptions = buildUpgradeOptions(activeSubscription, plans);

  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  useRemotePage({
    id: "account",
    title: "Account",
    subtitle: me ? "Subscriptions, owned games, and play history" : "Sign in to view your account",
    state: {
      signedIn: Boolean(me),
      ownedGames: ownerships.length,
      previousSubscriptions: previousSubscriptions.length,
      historyEntries: playHistory.length + tournamentHistory.length,
    },
    sections: [{ title: "Account sections", controls: [
      { id: "account.active", type: "button", label: "Active plan", disabled: !me },
      { id: "account.previous", type: "button", label: "Previous plans", disabled: !me },
      { id: "account.owned", type: "button", label: "Owned games", disabled: !me },
      { id: "account.history", type: "button", label: "Play history", disabled: !me },
      { id: "account.plans", type: "button", label: "View plans" },
    ] }],
    actions: {
      "account.active": () => jump("account-active"),
      "account.previous": () => jump("account-previous"),
      "account.owned": () => jump("account-owned"),
      "account.history": () => jump("account-history"),
      "account.plans": () => navigate("/subscriptions"),
    },
  });

  const handleCancelSubscription = () => {
    if (!paymentsEnabled) return;
    if (!window.confirm("Cancel your active subscription at the end of the current billing period?")) return;
    cancelSubscription.mutate();
  };

  const handleUpgradeSubscription = (option) => {
    if (!paymentsEnabled) return;
    setChangingOptionKey(option.key);
    subscriptionChange.mutate(
      {
        planId: option.planId,
        billingInterval: option.billingInterval,
      },
      {
        onSettled: () => setChangingOptionKey(null),
      }
    );
  };

  if (isMeLoading) {
    return <main className="flex-grow px-4 py-16 text-indigo-100/75">Loading account...</main>;
  }

  if (!me) {
    return (
      <main className="flex-grow px-4 py-16">
        <section className="mx-auto max-w-xl rounded-3xl border border-white/10 bg-indigo-950/45 p-8 text-center shadow-xl shadow-slate-950/25">
          <h1 className="text-3xl font-black text-white">Account</h1>
          <p className="mt-4 text-indigo-100/75">Sign in to see your subscriptions, games, and play history.</p>
          <Link
            to="/login"
            className="mt-6 inline-flex rounded-full bg-pink-500 px-8 py-3 text-sm font-black uppercase tracking-[0.28em] text-white transition hover:bg-pink-400"
          >
            Login
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="flex-grow px-4 py-12">
      <section className="mx-auto max-w-6xl text-left">
        <div className="flex flex-col gap-3 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.45em] text-sky-200/80">Account</p>
          <h1 className="text-4xl font-black tracking-[0.04em] text-white">Your Pocket Play</h1>
          <p className="text-indigo-100/70">{me.username || me.email || "Player"}</p>
        </div>

        <div id="account-active" className="mt-10 grid scroll-mt-24 gap-7">
          {subscriptionsQuery.isPending ? (
            <div className="rounded-3xl border border-white/10 bg-indigo-950/45 p-6 text-indigo-100/75">
              Loading subscriptions...
            </div>
          ) : activeSubscription ? (
            <>
              <SubscriptionCard
                subscription={activeSubscription}
                active
                upgradeOptions={upgradeOptions}
                isCanceling={cancelSubscription.isPending}
                isChanging={subscriptionChange.isPending}
                changingOptionKey={changingOptionKey}
                billingChangesEnabled={paymentsEnabled}
                onCancel={handleCancelSubscription}
                onUpgradeOption={handleUpgradeSubscription}
              />
              {subscriptionChange.isSuccess ? (
                <div className="rounded-3xl border border-emerald-300/25 bg-emerald-400/10 p-4 text-emerald-100">
                  Your subscription upgrade is scheduled for the end of the current billing period.
                </div>
              ) : null}
              {subscriptionChange.isError ? (
                <div className="rounded-3xl border border-pink-300/25 bg-pink-500/10 p-4 text-pink-100">
                  {subscriptionChange.error?.response?.data?.detail || "Could not schedule subscription upgrade."}
                </div>
              ) : null}
              {cancelSubscription.isError ? (
                <div className="rounded-3xl border border-pink-300/25 bg-pink-500/10 p-4 text-pink-100">
                  {cancelSubscription.error?.response?.data?.detail || "Could not cancel subscription."}
                </div>
              ) : null}
            </>
          ) : (
            <div className="rounded-3xl border border-white/10 bg-indigo-950/45 p-6 text-center">
              <h2 className="text-2xl font-black text-white">No active subscription</h2>
              <p className="mt-3 text-indigo-100/70">Subscribe to unlock the games library.</p>
              <Link
                to="/subscriptions"
                className="mt-5 inline-flex rounded-full bg-sky-300 px-6 py-3 text-sm font-black uppercase tracking-[0.25em] text-slate-950 transition hover:bg-cyan-200"
              >
                View Plans
              </Link>
            </div>
          )}
        </div>

        <section id="account-previous" className="mt-10 scroll-mt-24">
          <div className="flex items-end justify-between gap-4">
            <h2 className="text-2xl font-black text-white">Previous Subscriptions</h2>
            <span className="text-sm text-indigo-100/55">{previousSubscriptions.length} total</span>
          </div>
          <div className="mt-5 grid gap-5">
            {previousSubscriptions.length ? (
              previousSubscriptions.map((subscription) => (
                <SubscriptionCard key={subscription.id} subscription={subscription} />
              ))
            ) : (
              <div className="rounded-3xl border border-white/10 bg-indigo-950/35 p-6 text-indigo-100/65">
                No previous subscriptions yet.
              </div>
            )}
          </div>
        </section>

        <section id="account-owned" className="mt-10 scroll-mt-24">
          <div className="flex items-end justify-between gap-4">
            <h2 className="text-2xl font-black text-white">Owned Games</h2>
            <span className="text-sm text-indigo-100/55">{ownerships.length} total</span>
          </div>
          {ownershipsQuery.isPending ? (
            <div className="mt-5 rounded-3xl border border-white/10 bg-indigo-950/35 p-6 text-indigo-100/65">
              Loading owned games...
            </div>
          ) : ownerships.length ? (
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {ownerships.map((ownership) => (
                <OwnedGameCard key={ownership.id} ownership={ownership} />
              ))}
            </div>
          ) : (
            <div className="mt-5 rounded-3xl border border-white/10 bg-indigo-950/35 p-6 text-indigo-100/65">
              You have not bought any games yet.
            </div>
          )}
        </section>

        <section id="account-history" className="mt-10 scroll-mt-24">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="text-2xl font-black text-white">Playtime History</h2>
              <p className="mt-2 text-sm text-indigo-100/55">Sessions, rounds, scores, and winners.</p>
            </div>
            <span className="text-sm text-indigo-100/55">{playHistory.length + tournamentHistory.length} total</span>
          </div>
          <PlaytimeHistory
            sessions={playHistory}
            tournamentRuns={tournamentHistory}
            isPending={playHistoryQuery.isPending || tournamentHistoryQuery.isPending}
            error={playHistoryQuery.error || tournamentHistoryQuery.error}
          />
        </section>
      </section>
    </main>
  );
}
